import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { SxProps, Theme } from '@mui/system';
import LoadingButton from '@mui/lab/LoadingButton';
import { ButtonProps } from '@mui/material/Button';

type CustomButtonProps = ButtonProps & {
  'data-testid'?: string;
  loading?: boolean;
};

type Props = {
  buttons: CustomButtonProps[];
  content?: React.ReactNode;
  open: boolean;
  sx?: SxProps<Theme>;
  title: string;
};

export default function ConfirmationDialog(props: Readonly<Props>) {
  return (
    <Dialog open={props.open} sx={props.sx}>
      <DialogTitle data-testid="gds-vp-bpt-confirmation-dialog-title">
        {props.title}
      </DialogTitle>
      <DialogContent>{props.content}</DialogContent>
      <DialogActions>
        {props.buttons.map((b) => (
          <LoadingButton {...b} />
        ))}
      </DialogActions>
    </Dialog>
  );
}

export type ConfirmationDialogProviderProps = {
  hideConfirmationDialog: () => void;
  showConfirmationDialog: (props: Omit<Props, 'open'>) => void;
};
export const ConfirmationDialogContext =
  createContext<ConfirmationDialogProviderProps>({
    hideConfirmationDialog: () => undefined,
    showConfirmationDialog: (props: Omit<Props, 'open'>) => undefined,
  });

const initialState: Props = {
  buttons: [],
  content: null,
  open: false,
  title: '',
};
export function ConfirmationDialogProvider(
  props: Readonly<{ children: ReactNode }>
) {
  const [confirmationDialog, setConfirmationDialog] =
    useState<Props>(initialState);

  const showConfirmationDialog = useCallback((props: Omit<Props, 'open'>) => {
    setConfirmationDialog({ ...props, open: true });
  }, []);

  const hideConfirmationDialog = useCallback(() => {
    setConfirmationDialog(initialState);
  }, []);

  const valueProps = useMemo(
    () => ({ hideConfirmationDialog, showConfirmationDialog }),
    [hideConfirmationDialog, showConfirmationDialog]
  );

  return (
    <>
      {confirmationDialog.open && (
        <ConfirmationDialog {...confirmationDialog} />
      )}

      <ConfirmationDialogContext.Provider
        value={valueProps as ConfirmationDialogProviderProps}
      >
        {props.children}
      </ConfirmationDialogContext.Provider>
    </>
  );
}
